import { useAtom } from "jotai";
import { DateTime } from "luxon";
import { useContext, useState } from "react";
import { leadsFiltersAtom } from "../atoms/leadListAtoms";
import useLeads, { filtersByProduct, LeadPageContext } from "../useLeads";
import { LEAD_PRESETS, PERIOD_SHORTCUTS, isPresetActive } from "./leadPresets";

const useLeadFilters = () => {
  const { productCategory } = useContext(LeadPageContext);
  const [leadsFilters, setLeadsFilters] = useAtom(leadsFiltersAtom);
  const { actions } = useLeads();
  const [showExpiredManagers, setShowExpiredManagers] = useState(false);

  const resetLeadsFilters = () => {
    setLeadsFilters(filtersByProduct[productCategory]);
  };

  const updatePage = (page) => {
    const newFilters = { ...leadsFilters, page };
    actions.getLeads(newFilters);
    setLeadsFilters(newFilters);
  };

  const updateFilters = (e) => {
    const { name, value } = e.target;
    setLeadsFilters({
      ...leadsFilters,
      filters: { ...leadsFilters.filters, [name]: value },
    });
  };

  const updateOneMonth = () => {
    const today = DateTime.now().toISO();
    const minusOneMonth = DateTime.now().minus({ days: 30 }).endOf("day").toISO();
    setLeadsFilters({
      ...leadsFilters,
      filters: { ...leadsFilters.filters, beginAt: minusOneMonth, endAt: today },
    });
  };

  const toggleShowExpiredManagers = () => {
    setShowExpiredManagers(!showExpiredManagers);
  };

  // --- Nouveau : applique un PATCH de filtres et relance la recherche tout de suite.
  const applyAndSearch = (patch) => {
    const newFilters = {
      ...leadsFilters,
      page: 1,
      filters: { ...leadsFilters.filters, ...patch },
    };
    setLeadsFilters(newFilters);
    actions.getLeads(newFilters);
  };

  // --- Nouveau : presets exclusifs. Re-cliquer le preset actif le désactive.
  const applyPreset = (preset, ctx) => {
    const patch = preset.build(ctx);
    if (isPresetActive(preset, leadsFilters.filters, ctx)) {
      // désactive : remet à vide les champs pilotés par ce preset
      const emptied = Object.keys(patch).reduce((acc, key) => {
        acc[key] = Array.isArray(patch[key]) ? [] : "";
        return acc;
      }, {});
      applyAndSearch(emptied);
    } else {
      applyAndSearch(patch);
    }
  };

  const getActivePresetId = (ctx) => {
    const found = LEAD_PRESETS.find(
      (p) => p.isAvailable(ctx) && isPresetActive(p, leadsFilters.filters, ctx),
    );
    return found ? found.id : null;
  };

  // --- Nouveau : retire un jeton (clear = patch déjà calculé) et relance.
  const removeFilterToken = (clearPatch) => applyAndSearch(clearPatch);

  // --- Nouveau : tout effacer = reset complet + recherche.
  const clearAllFilters = () => {
    const fresh = filtersByProduct[productCategory];
    setLeadsFilters(fresh);
    actions.getLeads(fresh);
  };

  // --- Nouveau : applique un raccourci de période et relance.
  const setPeriodShortcut = (shortcutId) => {
    const sc = PERIOD_SHORTCUTS.find((s) => s.id === shortcutId);
    if (sc) applyAndSearch(sc.build());
  };

  return {
    leadsFilters,
    setLeadsFilters,
    showExpiredManagers,
    actions: {
      resetLeadsFilters,
      updateFilters,
      updatePage,
      updateOneMonth,
      toggleShowExpiredManagers,
      applyAndSearch,
      applyPreset,
      getActivePresetId,
      removeFilterToken,
      clearAllFilters,
      setPeriodShortcut,
    },
  };
};

export default useLeadFilters;
